import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { apiLogin, apiLogout, apiMe, apiRegister, http, setAccessToken } from "../lib/api";
import { logger } from "../lib/logger";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = unauth, object = auth
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    // Skip auth bootstrap on public share routes — they don't need a session
    // and the 401 round-trip pollutes logs / wastes a request inside Power BI iframes.
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (path.startsWith("/share/")) {
      setUser(null);
      setBootstrapped(true);
      return;
    }
    (async () => {
      try {
        const me = await apiMe();
        setUser(me);
      } catch {
        setUser(null);
      } finally {
        setBootstrapped(true);
      }
    })();
  }, []);

  // Sync browser tab title with the currently active company.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const company = user?.company_name;
    document.title = company ? `Cornerstone | ${company}` : "Cornerstone";
  }, [user?.company_name]);

  const login = async (email, password) => {
    const data = await apiLogin(email, password);
    if (data?.access_token) setAccessToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const data = await apiRegister(payload);
    if (data?.access_token) setAccessToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await apiLogout(); }
    catch (e) {
      // Server-side logout failed (already expired / network). We still clear
      // local auth state below so the UI matches reality.
      logger.debug("apiLogout failed:", e?.response?.status || e?.message);
    }
    setAccessToken(null);
    setUser(null);
  };

  const switchCompany = async (companyId) => {
    const r = await http.post("/auth/switch-company", { company_id: companyId });
    setUser(r.data);
    return r.data;
  };

  const value = useMemo(
    () => ({ user, login, register, logout, bootstrapped, setUser, switchCompany }),
    [user, bootstrapped],
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
